import { useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import SelectTextField from '../../../molecules/SelectTextField';
import TextEdit from '../../../molecules/TextEdit';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { Beneficiary, SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';

interface BeneficiarySelectFieldProps {
    label?: string;
    value: string;
    onChange: (beneficiaryId: string) => void;
    beneficiaries: Beneficiary[];
    beneficiaryTypeOptions: SelectOption[];
    // Cadastra um beneficiário novo e devolve o criado (para já selecioná-lo). Null em erro.
    // Opcional: só é chamado quando `allowCreate` é true.
    onCreate?: (data: { name: string; beneficiaryType?: string }) => Promise<Beneficiary | null>;
    isSaving: boolean;
    errorFields: ErrorField[];
    disabled?: boolean;
    // Habilita o "+ Novo" inline. Falso no escritório responsável do caso: lá o beneficiário
    // exige cadastro completo (documento, contatos, vínculo person), feito na tela de
    // Beneficiários — o mini-form inline criaria um registro pela metade. Default: true.
    allowCreate?: boolean;
    // Nome do campo do 422 a exibir no select. Na despesa o backend valida como `beneficiaryId`
    // (default); no caso, como `responsibleFirmId` — cada contexto passa a sua chave, senão o
    // erro de validação daquele contexto não apareceria no campo.
    errorFieldName?: string;
}

// Select de beneficiário com opção "+ Novo" inline: o usuário escolhe um beneficiário
// cadastrado ou abre um mini-form que cadastra e já seleciona o novo — sem sair da tela de
// despesa (pedido do usuário). O mini-form só pede nome e tipo; o cadastro completo (documento,
// observações) vive na página de Beneficiários.
const BeneficiarySelectField = ({
    label = 'Beneficiário (opcional)',
    value,
    onChange,
    beneficiaries,
    beneficiaryTypeOptions,
    onCreate,
    isSaving,
    errorFields,
    disabled = false,
    allowCreate = true,
    errorFieldName = 'beneficiaryId',
}: BeneficiarySelectFieldProps) => {
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newType, setNewType] = useState(beneficiaryTypeOptions[0]?.value || 'OTHER');

    const options: SelectOption[] = beneficiaries.map((b) => ({
        value: b.id,
        label: b.beneficiaryTypeFormatted ? `${b.name} — ${b.beneficiaryTypeFormatted}` : b.name,
    }));

    const openCreate = () => {
        setNewName('');
        setNewType(beneficiaryTypeOptions[0]?.value || 'OTHER');
        setCreating(true);
    };

    const submitCreate = async () => {
        if (!onCreate) return;
        const created = await onCreate({ name: newName, beneficiaryType: newType });
        if (created) {
            onChange(created.id);
            setCreating(false);
        }
    };

    if (creating) {
        return (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 p-3 space-y-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Novo beneficiário</p>
                <TextEdit
                    label="Nome"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'name')}
                />
                <SelectTextField
                    label="Tipo"
                    items={beneficiaryTypeOptions}
                    value={newType}
                    onChange={(e) => setNewType(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'beneficiaryType')}
                />
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => setCreating(false)}
                        className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={submitCreate}
                        disabled={isSaving}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                    >
                        {isSaving ? 'Salvando...' : 'Cadastrar e selecionar'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <SelectTextField
                label={label}
                items={options}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                emptyLabel="Sem beneficiário"
                disabled={disabled}
                errorMessage={getErrorMessage(errorFields, errorFieldName)}
            />
            {allowCreate && (
                <button
                    type="button"
                    onClick={openCreate}
                    disabled={disabled}
                    className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                >
                    <FaPlus size={9} /> Novo beneficiário
                </button>
            )}
        </div>
    );
};

export default BeneficiarySelectField;
